import React, { useEffect, useRef, useState } from "react";
import { Pencil, Loader2, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatINR, fmtDate } from "@/lib/format";
import { SuggestionMark } from "./SuggestionRow";

// reads as text until clicked · Enter saves · Esc cancels · optimistic save with 600ms green flash · failure → revert + inline red + Retry
export function InlineField({ label, value, type = "text", options = [], onSave, format, editable = true, warning, suggested, onAcceptSuggested, testid, className }) {
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [state, setState] = useState("idle"); // idle | saving | flash | error
  const ref = useRef(null);
  useEffect(() => { if (!edit) setDraft(value ?? ""); }, [value, edit]);
  useEffect(() => { if (edit) ref.current?.focus(); }, [edit]);
  const shown = format ? format(value) : type === "currency" ? formatINR(value) : type === "date" ? fmtDate(value) : (value ?? "—");
  const commit = async (v) => {
    setEdit(false);
    if ((v ?? "") === (value ?? "")) return;
    setState("saving");
    try { await onSave?.(v === "" ? null : v); setState("flash"); setTimeout(() => setState("idle"), 600); }
    catch { setState("error"); }
  };
  const input = type === "select" ? (
    <select ref={ref} value={draft} onChange={(e) => commit(e.target.value)} onBlur={() => setEdit(false)} onKeyDown={(e) => e.key === "Escape" && setEdit(false)}
      className="h-ctl rounded-chip border border-input bg-card text-ui-sm px-2 focus:outline-none focus:ring-2 focus:ring-ring" data-testid={testid && `${testid}-input`}>
      <option value="">—</option>{options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  ) : (
    <input ref={ref} type={type === "currency" ? "number" : type} value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={() => commit(draft)}
      onKeyDown={(e) => { if (e.key === "Enter") commit(draft); if (e.key === "Escape") setEdit(false); }}
      className="h-ctl rounded-chip border border-input bg-card text-ui-sm px-2 w-full focus:outline-none focus:ring-2 focus:ring-ring" data-testid={testid && `${testid}-input`} />
  );
  return (
    <div className={cn("group font-ui", className)} data-testid={testid}>
      {label && <div className="text-ui-2xs uppercase tracking-wide text-muted-foreground mb-0.5">{label}</div>}
      {edit ? input : (
        <button type="button" disabled={!editable} onClick={() => editable && setEdit(true)} data-testid={testid && `${testid}-value`}
          className={cn("flex items-center gap-1.5 text-ui-base text-left min-h-[24px] px-1 -mx-1 rounded-[2px] transition-colors duration-120", editable && "hover:bg-muted", state === "flash" && "v2-flash")}>
          <span className={cn(suggested != null && "line-through text-muted-foreground")}>{shown}</span>
          {suggested != null && <span className="inline-flex items-center gap-1 text-suggest"><SuggestionMark /> {format ? format(suggested) : suggested}</span>}
          {state === "saving" && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
          {editable && state === "idle" && <Pencil className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-120" />}
        </button>
      )}
      {suggested != null && onAcceptSuggested && !edit && <button onClick={onAcceptSuggested} className="text-ui-xs text-primary hover:underline">Accept suggestion</button>}
      {warning && <div className="flex items-center gap-1 text-ui-xs text-warning mt-0.5" data-testid={testid && `${testid}-warning`}><AlertTriangle className="w-3 h-3" /> {warning}</div>}
      {state === "error" && <div className="text-ui-xs text-destructive mt-0.5">Couldn't save. <button onClick={() => commit(draft)} className="underline">Retry</button></div>}
    </div>
  );
}
